import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  IconButton,
  Chip,
  Tooltip,
  Stack,
  CircularProgress,
  Button,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider,
  Paper,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ExchangeTag from '../Common/ExchangeTag';
import RefreshIcon from '@mui/icons-material/Refresh';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { enqueueSnackbar } from 'notistack';
import {
  fetchAlertsAction,
  fetchTriggeredAlertsAction,
  deleteAlertAction,
  pausePlayAlertAction,
  openPriceAlertModal,
} from '../../store/actions/alerts';
import useAlertLtp from './AlertRowLtp';
import PageTabs from '../Common/PageTabs';

const formatDateTime = (input) => {
  if (!input) return '—';
  const ts = typeof input === 'number' ? (input > 1e12 ? input : input * 1000) : input;
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const statusColor = (status) => {
  switch ((status || '').toLowerCase()) {
    case 'running': return 'success';
    case 'paused': return 'warning';
    case 'triggered': return 'info';
    default: return 'default';
  }
};

const ActiveAlertRow = ({ alert, onEdit, onDelete, onTogglePause }) => {
  const isRunning = (alert.status || '').toLowerCase() === 'running';
  const { ltp, percentChange } = useAlertLtp({
    exchange: alert.exchange,
    token: alert.token || alert.instrument_token,
    initialLtp: alert.ltp,
  });

  return (
    <TableRow hover>
      <TableCell>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" fontWeight={600}>{alert.trading_symbol}</Typography>
          {alert.exchange && <ExchangeTag exchange={alert.exchange} />}
        </Stack>
      </TableCell>
      <TableCell>
        <Typography variant="body2">{alert.user_message || '—'}</Typography>
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2" fontWeight={600}>
          {ltp != null ? `₹${Number(ltp).toFixed(2)}` : '—'}
        </Typography>
        {percentChange != null && (
          <Typography
            variant="caption"
            color={Number(percentChange) >= 0 ? 'success.main' : 'error.main'}
          >
            {Number(percentChange) >= 0 ? '+' : ''}{Number(percentChange).toFixed(2)}%
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <Chip
          label={alert.frequency || 'once'}
          size="small"
          variant="outlined"
        />
      </TableCell>
      <TableCell>
        <Chip
          label={alert.status || '—'}
          size="small"
          color={statusColor(alert.status)}
        />
      </TableCell>
      <TableCell>
        <Typography variant="caption">{formatDateTime(alert.expiry)}</Typography>
      </TableCell>
      <TableCell align="right">
        <Tooltip title="Modify alert">
          <IconButton size="small" onClick={() => onEdit(alert)}><EditIcon fontSize="small" /></IconButton>
        </Tooltip>
        <Tooltip title={isRunning ? 'Pause alert' : 'Resume alert'}>
          <IconButton size="small" onClick={() => onTogglePause(alert)}>
            {isRunning ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete alert">
          <IconButton size="small" onClick={() => onDelete(alert)}><DeleteIcon fontSize="small" /></IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
};

const ActiveAlertCard = ({ alert, onEdit, onDelete, onTogglePause }) => {
  const isRunning = (alert.status || '').toLowerCase() === 'running';
  const { ltp, percentChange } = useAlertLtp({
    exchange: alert.exchange,
    token: alert.token || alert.instrument_token,
    initialLtp: alert.ltp,
  });

  return (
    <Paper
      elevation={0}
      sx={(theme) => ({
        p: 2,
        mb: 1.5,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
      })}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" fontWeight={700}>
            {alert.trading_symbol}
          </Typography>
          {alert.exchange && <ExchangeTag exchange={alert.exchange} />}
        </Stack>
        <Chip
          label={alert.status || '—'}
          size="small"
          color={statusColor(alert.status)}
        />
      </Box>

      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
        {alert.user_message || '—'}
      </Typography>

      <Divider sx={{ my: 1.5 }} />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.5 }}>
        <Box>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
            LTP
          </Typography>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography variant="body2" fontWeight={600}>
              {ltp != null ? `₹${Number(ltp).toFixed(2)}` : '—'}
            </Typography>
            {percentChange != null && (
              <Typography
                variant="caption"
                color={Number(percentChange) >= 0 ? 'success.main' : 'error.main'}
                fontWeight={600}
              >
                ({Number(percentChange) >= 0 ? '+' : ''}{Number(percentChange).toFixed(2)}%)
              </Typography>
            )}
          </Stack>
        </Box>

        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
            Expiry
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
            {formatDateTime(alert.expiry)}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Chip label={alert.frequency || 'once'} size="small" variant="outlined" />
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Modify alert">
            <IconButton size="small" onClick={() => onEdit(alert)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={isRunning ? 'Pause alert' : 'Resume alert'}>
            <IconButton size="small" onClick={() => onTogglePause(alert)}>
              {isRunning ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete alert">
            <IconButton size="small" color="error" onClick={() => onDelete(alert)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Paper>
  );
};

const TriggeredAlertCard = ({ alert }) => {
  return (
    <Paper
      elevation={0}
      sx={(theme) => ({
        p: 2,
        mb: 1.5,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
      })}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" fontWeight={700}>
            {alert.trading_symbol}
          </Typography>
          {alert.exchange && <ExchangeTag exchange={alert.exchange} />}
        </Stack>
        <Chip label={alert.frequency || 'once'} size="small" variant="outlined" />
      </Box>

      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
        {alert.user_message || '—'}
      </Typography>

      <Divider sx={{ my: 1.5 }} />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.5 }}>
        <Box>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
            Triggered Price
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {alert.new_value != null ? `₹${Number(alert.new_value).toFixed(2)}` : '—'}
          </Typography>
        </Box>

        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
            Triggered On
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
            {formatDateTime(alert.update_entry_time || alert.generated_at)}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

const EmptyState = ({ icon, message }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, color: 'text.secondary', px: 2 }}>
    {icon}
    <Typography variant="body1" sx={{ mt: 2, textAlign: 'center' }}>{message}</Typography>
  </Box>
);

const PriceAlerts = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const arrAlertsData = useSelector((state) => state.alerts?.arrAlertsData || []);
  const triggeredAlerts = useSelector((state) => state.alerts?.triggeredAlerts || []);

  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const refresh = async () => {
    setLoading(true);
    await Promise.all([
      dispatch(fetchAlertsAction()),
      dispatch(fetchTriggeredAlertsAction()),
    ]);
    setLoading(false);
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeCount = arrAlertsData.length;
  const triggeredCount = triggeredAlerts.length;

  const handleEdit = (alert) => {
    dispatch(openPriceAlertModal({
      instrument: {
        token: alert.token || alert.instrument_token,
        exchange: alert.exchange,
        trading_symbol: alert.trading_symbol,
        display_name: alert.trading_symbol,
        ltp: alert.ltp,
      },
      isModify: true,
      existingAlert: alert,
    }));
  };

  const handleDelete = (alert) => {
    setDeleteTarget(alert);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget?.id) return;
    setDeleting(true);
    const res = await dispatch(deleteAlertAction(deleteTarget.id));
    setDeleting(false);
    setDeleteTarget(null);
    if (res?.status === 'success') {
      enqueueSnackbar('Alert deleted', { variant: 'success' });
    } else {
      enqueueSnackbar(res?.message || 'Failed to delete alert', { variant: 'error' });
    }
  };

  const handleTogglePause = async (alert) => {
    if (!alert?.id) return;
    const isRunning = (alert.status || '').toLowerCase() === 'running';
    const next = isRunning ? 'paused' : 'running';
    const res = await dispatch(pausePlayAlertAction(alert.id, { status: next }));
    if (res?.status === 'success') {
      enqueueSnackbar(isRunning ? 'Alert paused' : 'Alert resumed', { variant: 'success' });
    } else {
      enqueueSnackbar(res?.message || 'Failed to update alert', { variant: 'error' });
    }
  };

  return (
    <Box sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      backgroundColor: 'background.default',
      [theme.breakpoints.down('sm')]: {
        // 56 AppBar + 30 Top Ticker = 86 top; + 56 BottomNav = 142
        height: 'calc(100vh - 142px)',
        position: 'fixed',
        top: 86,
        left: 0,
        right: 0,
        bottom: 56,
      },
    }}>
      <PageTabs
        value={tab}
        onChange={(_e, v) => setTab(v)}
        tabs={[
          { label: `Active (${activeCount})`, value: 0 },
          { label: `Triggered (${triggeredCount})`, value: 1 },
        ]}
        sticky
        action={
          <Button startIcon={<RefreshIcon />} onClick={refresh} disabled={loading} size="small">
            Refresh
          </Button>
        }
      />

      <Box sx={{ flex: 1, overflow: 'auto', p: { xs: 1.5, sm: 2 } }}>
        {tab === 0 && (
          <Box>
            {loading && arrAlertsData.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress size={24} />
              </Box>
            ) : arrAlertsData.length === 0 ? (
              <EmptyState
                icon={<NotificationsActiveOutlinedIcon sx={{ fontSize: 48 }} />}
                message="No active price alerts. Create one from any watchlist row's more options menu."
              />
            ) : isMobile ? (
              <Box>
                {arrAlertsData.map((alert) => (
                  <ActiveAlertCard
                    key={alert.id}
                    alert={alert}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onTogglePause={handleTogglePause}
                  />
                ))}
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Instrument</TableCell>
                      <TableCell>Condition</TableCell>
                      <TableCell align="right">LTP</TableCell>
                      <TableCell>Frequency</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Expiry</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {arrAlertsData.map((alert) => (
                      <ActiveAlertRow
                        key={alert.id}
                        alert={alert}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onTogglePause={handleTogglePause}
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {tab === 1 && (
          <Box>
            {loading && triggeredAlerts.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress size={24} />
              </Box>
            ) : triggeredAlerts.length === 0 ? (
              <EmptyState
                icon={<NotificationsActiveOutlinedIcon sx={{ fontSize: 48 }} />}
                message="No triggered alerts yet."
              />
            ) : isMobile ? (
              <Box>
                {triggeredAlerts.map((alert) => (
                  <TriggeredAlertCard key={alert.id || alert.alert_id} alert={alert} />
                ))}
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Instrument</TableCell>
                      <TableCell>Condition</TableCell>
                      <TableCell align="right">Triggered at price</TableCell>
                      <TableCell>Frequency</TableCell>
                      <TableCell>Triggered on</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {triggeredAlerts.map((alert) => (
                      <TableRow key={alert.id || alert.alert_id} hover>
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2" fontWeight={600}>
                              {alert.trading_symbol}
                            </Typography>
                            {alert.exchange && <ExchangeTag exchange={alert.exchange} />}
                          </Stack>
                        </TableCell>
                        <TableCell>{alert.user_message || '—'}</TableCell>
                        <TableCell align="right">
                          {alert.new_value != null ? `₹${Number(alert.new_value).toFixed(2)}` : '—'}
                        </TableCell>
                        <TableCell>
                          <Chip label={alert.frequency || 'once'} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">{formatDateTime(alert.update_entry_time || alert.generated_at)}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </Box>
      {/* Modal is mounted globally via PriceAlertModalHOC; this page only dispatches openPriceAlertModal. */}
      
      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete alert?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the price alert for{" "}
            <strong>
              {deleteTarget?.trading_symbol}
            </strong>
            ?
            <br />
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Keep
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? "Deleting..." : "Delete Alert"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PriceAlerts;
